import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";

/**
 * Compact, calm KPI card used on Home + KPI dashboard.
 *
 * Visual language:
 *  - subtle icon chip (10% tint of brand color)
 *  - large light-weight number (font-heading)
 *  - one-line metadata (rule label OR "not connected" pill)
 *  - tiny sparkline of the last ~10 weeks (area, very low contrast)
 *  - sage/terracotta trend pill — only when a real delta exists
 *
 * Designed to feel "ministry operations" — calm, scannable, never finance-y.
 */
export default function KPICard({
  label,
  value,
  icon: Icon,
  color = "#425252",
  delta = null,            // number | null  (percentage)
  deltaLabel = "",         // e.g. "vs last week"
  notConnected = false,
  notConnectedLabel = "Not connected",
  footnote = "",
  sparkline = [],          // array of { value: number }
  href,
  testId,
}) {
  const Trend = delta == null
    ? null
    : delta > 0.5 ? TrendingUp
    : delta < -0.5 ? TrendingDown
    : Minus;
  const trendTone =
    delta == null ? ""
    : delta > 0.5 ? "text-sage bg-sage/15"
    : delta < -0.5 ? "text-terracotta bg-terracotta/10"
    : "text-brand-primary/55 bg-brand-primary/5";

  const inner = (
    <>
      <div className="flex items-center gap-3 mb-4 min-w-0">
        <div
          className="h-10 w-10 rounded-xl grid place-items-center shrink-0 transition-transform duration-300 group-hover:scale-105"
          style={{ background: `${color}1A` }}
        >
          <Icon className="h-5 w-5" style={{ color }} strokeWidth={1.7} />
        </div>
        <p className="text-sm text-brand-primary/70 font-medium truncate">{label}</p>
      </div>

      <div className="flex items-end justify-between gap-3">
        <p
          className="font-heading text-3xl font-light text-brand-primary tracking-tight leading-none"
          data-testid={testId ? `${testId}-value` : undefined}
        >
          {value}
        </p>
        {Trend && !notConnected && (
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 ${trendTone}`}
            data-testid={testId ? `${testId}-delta` : undefined}
          >
            <Trend className="h-3 w-3" strokeWidth={2.2} />
            {Math.abs(delta).toFixed(0)}%
          </span>
        )}
      </div>

      {/* Sparkline (sits between number and footer; very subtle) */}
      <div className="mt-3 h-8 -mx-1">
        {sparkline && sparkline.length >= 2 && !notConnected ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkline} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
              <defs>
                <linearGradient id={`spark-${testId || label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={1.6}
                fill={`url(#spark-${testId || label})`}
                isAnimationActive
                animationDuration={650}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-end">
            <div className="h-px w-full bg-brand-primary/5 rounded-full" />
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 min-h-[18px]">
        {notConnected ? (
          <span
            className="text-[10px] uppercase tracking-wider font-semibold text-terracotta"
            data-testid={testId ? `${testId}-not-connected` : undefined}
          >
            {notConnectedLabel}
          </span>
        ) : (
          <p className="text-[10px] uppercase tracking-wider text-brand-primary/50 font-semibold truncate">
            {deltaLabel || footnote}
          </p>
        )}
      </div>
    </>
  );

  const baseClass =
    "group block rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg hover:border-brand-primary/20";

  if (href) {
    return (
      <Link to={href} data-testid={testId} className={baseClass}>
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testId} className={baseClass}>
      {inner}
    </div>
  );
}

/** Skeleton placeholder shown while /kpi-summary is loading. Same footprint as KPICard. */
export function KPICardSkeleton() {
  return (
    <div data-testid="kpi-card-skeleton" className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 sm:p-6 animate-pulse">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-brand-primary/5" />
        <div className="h-3 w-24 rounded-full bg-brand-primary/5" />
      </div>
      <div className="h-7 w-28 rounded-md bg-brand-primary/10" />
      <div className="mt-3 h-8 w-full rounded-md bg-brand-primary/[0.04]" />
      <div className="mt-3 h-2 w-20 rounded-full bg-brand-primary/5" />
    </div>
  );
}
